import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Bug, Lightbulb, MessageCircle, Image as ImageIcon, Trash2, Loader2, ExternalLink,
  MessageSquare, RefreshCw,
} from 'lucide-react';

const TYPE_META = {
  bug: { icon: Bug, color: '#FF6B35' },
  idea: { icon: Lightbulb, color: '#2EE59D' },
  general: { icon: MessageCircle, color: '#00B4D8' },
};
const STATUS_COLORS = {
  new: '#00B4D8', in_progress: '#FFB020', resolved: '#2EE59D', rejected: '#9AA4AF',
};
const STATUSES = ['new', 'in_progress', 'resolved', 'rejected'];

export const AdminFeedback = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, new: 0, in_progress: 0, resolved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(true);
  const [fType, setFType] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [shotUrls, setShotUrls] = useState({});
  const [notesDraft, setNotesDraft] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (fType !== 'all') params.type = fType;
      if (fStatus !== 'all') params.status = fStatus;
      const { data } = await api.get('/admin/feedback', { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      console.error('feedback load failed', e);
      toast.error(t('feedbackAdmin.loadError'));
    } finally {
      setLoading(false);
    }
  }, [fType, fStatus, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/admin/feedback/settings')
      .then(({ data }) => setEnabled(!!data.enabled))
      .catch((e) => console.debug('feedback settings load failed', e));
  }, []);

  const toggleWidget = async (val) => {
    setEnabled(val);
    try {
      await api.put('/admin/feedback/settings', { enabled: val });
      toast.success(val ? t('feedbackAdmin.widgetOn') : t('feedbackAdmin.widgetOff'));
    } catch (e) {
      setEnabled(!val);
      toast.error(t('feedbackAdmin.loadError'));
    }
  };

  const changeStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(t('feedbackAdmin.statusUpdated'));
      load();
    } catch (e) { toast.error(t('feedbackAdmin.loadError')); }
  };

  const saveNotes = async (id) => {
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { admin_notes: notesDraft[id] ?? '' });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(t('feedbackAdmin.notesSaved'));
    } catch (e) { toast.error(t('feedbackAdmin.loadError')); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/feedback/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success(t('feedbackAdmin.deleted'));
      load();
    } catch (e) { toast.error(t('feedbackAdmin.loadError')); }
  };

  const loadShot = async (id) => {
    if (shotUrls[id]) return;
    try {
      const res = await api.get(`/admin/feedback/${id}/screenshot`, { responseType: 'blob' });
      setShotUrls((prev) => ({ ...prev, [id]: URL.createObjectURL(res.data) }));
    } catch (e) { toast.error(t('feedbackAdmin.loadError')); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-5" data-testid="admin-feedback">
      {/* header + toggle */}
      <div className="kite-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-display text-2xl flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#00B4D8]" /> {t('feedbackAdmin.title')}</h3>
          <div className="flex flex-wrap gap-2 mt-2 text-xs">
            <span className="text-muted-foreground">{counts.total || 0} {t('feedbackAdmin.total')}</span>
            <span className="text-[#00B4D8]">{counts.new || 0} {t('feedbackAdmin.statusNew')}</span>
            <span className="text-[#FFB020]">{counts.in_progress || 0} {t('feedbackAdmin.statusInProgress')}</span>
            <span className="text-[#2EE59D]">{counts.resolved || 0} {t('feedbackAdmin.statusResolved')}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 rounded-xl bg-[#0A0A0A] border border-white/10 px-4 py-3">
          <div>
            <div className="text-sm font-medium" data-testid="feedback-widget-state">{enabled ? t('feedbackAdmin.widgetOn') : t('feedbackAdmin.widgetOff')}</div>
            <div className="text-xs text-muted-foreground">{t('feedbackAdmin.widgetHint')}</div>
          </div>
          <Switch checked={enabled} onCheckedChange={toggleWidget} data-testid="feedback-widget-toggle" />
        </div>
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Select value={fType} onValueChange={setFType}>
          <SelectTrigger className="w-40 bg-[#0A0A0A] border-white/15" data-testid="feedback-filter-type"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#12161B] border-white/15 text-white">
            <SelectItem value="all">{t('feedbackAdmin.allTypes')}</SelectItem>
            <SelectItem value="bug">{t('feedbackAdmin.type_bug')}</SelectItem>
            <SelectItem value="idea">{t('feedbackAdmin.type_idea')}</SelectItem>
            <SelectItem value="general">{t('feedbackAdmin.type_general')}</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger className="w-44 bg-[#0A0A0A] border-white/15" data-testid="feedback-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#12161B] border-white/15 text-white">
            <SelectItem value="all">{t('feedbackAdmin.allStatuses')}</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`feedbackAdmin.status${s.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join('')}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={load} className="text-muted-foreground hover:text-white" data-testid="feedback-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> {t('feedbackAdmin.refresh')}
        </Button>
      </div>

      {/* list */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>
      ) : items.length === 0 ? (
        <div className="kite-card p-10 text-center text-muted-foreground" data-testid="feedback-empty">{t('feedbackAdmin.empty')}</div>
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const meta = TYPE_META[it.type] || TYPE_META.general;
            const Icon = meta.icon;
            return (
              <div key={it.id} className="kite-card p-4" data-testid="feedback-item">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${meta.color}1f` }}><Icon className="w-4 h-4" style={{ color: meta.color }} /></span>
                    <Badge variant="outline" className="border-white/15" style={{ color: meta.color }}>{t(`feedbackAdmin.type_${it.type}`)}</Badge>
                    <span className="text-xs text-muted-foreground">{fmtDate(it.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={it.status} onValueChange={(v) => changeStatus(it.id, v)}>
                      <SelectTrigger className="h-8 w-40 bg-[#0A0A0A] border-white/15 text-xs" data-testid="feedback-item-status">
                        <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: STATUS_COLORS[it.status] }} /><SelectValue /></span>
                      </SelectTrigger>
                      <SelectContent className="bg-[#12161B] border-white/15 text-white">
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`feedbackAdmin.status${s.split('_').map((w) => w[0].toUpperCase() + w.slice(1)).join('')}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-[#FF6B35]" data-testid="feedback-delete"><Trash2 className="w-4 h-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#12161B] border-white/10 text-white">
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('feedbackAdmin.deleteConfirmTitle')}</AlertDialogTitle>
                          <AlertDialogDescription className="text-muted-foreground">{t('feedbackAdmin.deleteConfirmDesc')}</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-white/15 hover:bg-white/10">{t('feedbackAdmin.cancel')}</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(it.id)} className="bg-[#FF6B35] hover:bg-[#ff8255] text-black" data-testid="feedback-delete-confirm">{t('feedbackAdmin.delete')}</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>

                <p className="mt-3 text-sm text-white/90 whitespace-pre-wrap" data-testid="feedback-item-message">{it.message}</p>

                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span>{t('feedbackAdmin.user')}: <span className="text-white/70">{it.user_email || it.user_name || it.user_id}</span></span>
                  {it.page_url && <span>{t('feedbackAdmin.page')}: <span className="text-white/70 font-data">{it.page_url}</span></span>}
                </div>

                {/* screenshot */}
                {it.has_screenshot && (
                  <div className="mt-3">
                    {shotUrls[it.id] ? (
                      <a href={shotUrls[it.id]} target="_blank" rel="noreferrer" className="inline-block">
                        <img src={shotUrls[it.id]} alt="screenshot" className="max-h-48 rounded-lg border border-white/10" data-testid="feedback-screenshot-img" />
                        <span className="flex items-center gap-1 text-xs text-[#48CAE4] mt-1">{t('feedbackAdmin.openFull')} <ExternalLink className="w-3 h-3" /></span>
                      </a>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => loadShot(it.id)} className="border-white/15 bg-white/5 hover:bg-white/10" data-testid="feedback-view-screenshot">
                        <ImageIcon className="w-4 h-4 mr-1.5" /> {t('feedbackAdmin.viewScreenshot')}
                      </Button>
                    )}
                  </div>
                )}

                {/* notes */}
                <div className="mt-3 pt-3 border-t border-white/8">
                  <Textarea
                    defaultValue={it.admin_notes || ''}
                    onChange={(e) => setNotesDraft((p) => ({ ...p, [it.id]: e.target.value }))}
                    placeholder={t('feedbackAdmin.notesPlaceholder')}
                    rows={2}
                    className="bg-[#0A0A0A] border-white/15 resize-none text-sm"
                    data-testid="feedback-notes"
                  />
                  <div className="flex justify-end mt-2">
                    <Button size="sm" variant="ghost" onClick={() => saveNotes(it.id)} className="text-[#48CAE4] hover:text-white hover:bg-white/10" data-testid="feedback-save-notes">{t('feedbackAdmin.saveNotes')}</Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminFeedback;
